/**
 * Dashboard Page Component
 * Main authenticated page: what's overdue, due today and coming up
 */

import { useState } from 'react';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../hooks/useAuth';
import { useHouseholdRoles } from '../hooks/useHouseholdRoles';
import { greetingFor } from '../lib/dateUtils';
import ChoreList from './ChoreList';
import AddChoreForm from './AddChoreForm';
import { Button, cx, focusRing } from './ui';

export function DashboardPage() {
  const { user, isLocal } = useAuth();
  // A child everywhere has no household to add a chore to.
  const { childEverywhere } = useHouseholdRoles();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const now = new Date();
  // "Me" is a placeholder, not a name to greet.
  const firstName = isLocal ? undefined : user?.displayName?.trim().split(/\s+/)[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <h1 className="mt-0.5 text-2xl font-bold tracking-tight text-gray-900 text-balance dark:text-gray-100">
            {greetingFor(now)}{firstName ? `, ${firstName}` : ''}
          </h1>
        </div>
        {!childEverywhere && (
          <Button variant="primary" icon={PlusIcon} onClick={() => setIsAddModalOpen(true)} className="hidden sm:inline-flex">
            Add chore
          </Button>
        )}
      </div>

      {/* On a phone, Add chore floats above the tab bar, where the thumb is. */}
      {!childEverywhere && <button
        type="button"
        onClick={() => setIsAddModalOpen(true)}
        aria-label="Add chore"
        className={cx(
          'fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-30 flex h-14 w-14 items-center justify-center rounded-2xl',
          'bg-indigo-600 text-white shadow-lg shadow-indigo-600/40 transition-colors hover:bg-indigo-700 active:bg-indigo-800 dark:shadow-none sm:hidden',
          focusRing,
        )}
      >
        <PlusIcon className="h-7 w-7" aria-hidden="true" />
      </button>}

      <ChoreList onAddChore={() => setIsAddModalOpen(true)} />

      <AddChoreForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </div>
  );
}
